import { AccountStatusType } from './account-type.enum';
import { DocumentActionCategoryType } from './common.enum';
import { CurrencyExchangeTransactionType, TransactionStatus, TransactionType } from './common.type';

export type AccountIdentifier = {
  accountNumber?: string;
  sortCode?: string;
  providerExtraInfo: ProviderExtraInfo;
  bic?: string;
  iban?: string;
  ledgerNumber?: string;
  aba?: string;
  ach?: string;
  bsbCode?: string;
  ifsc?: string;
  rtnCanada?: string;
  clabe?: string;
  bankCode?: string;
  branchCode?: string;
};

export type ProviderExtraInfo = {
  chaps?: {
    country: string;
    currency: string;
    routingCodes: {
      'sort-code': string;
    };
    accountNumber: string;
    iban?: string | null;
    bankName?: string | null;
    bankAddress?: string | null;
  };
  sepa?: {
    country: string;
    currency: string;
    routingCodes: {
      bic?: string;
    };
    iban: string;
    bankName?: string | null;
    bankAddress?: string | null;
  };
  'faster-payments'?: {
    country: string;
    currency: string;
    routingCodes: {
      'sort-code': string;
    };
    accountNumber: string;
    iban?: string | null;
    bankName?: string | null;
    bankAddress?: string | null;
  };
  swift?: {
    country: string;
    currency: string;
    routingCodes: {
      bic: string;
    };
    iban: string;
    bankName?: string | null;
    bankAddress?: string | null;
  };
  ach?: {
    country: string;
    currency: string;
    routingCodes: {
      aba: string;
    };
    accountNumber: string;
    bankName?: string | null;
    bankAddress?: string | null;
  };
  wire?: {
    country: string;
    currency: string;
    routingCodes: {
      aba: string;
    };
    accountNumber: string;
    bankName?: string | null;
    bankAddress?: string | null;
  };
};

export function emptyAccountInfoType(): AccountInfoType {
  return {
    accountType: 'current',
    name: '',
    alias: '',
    balance: '',
    availableBalance: '',
    indicativeBalance: '',
    ledgerBalance: '',
    ledgerBalanceIncludingPendingExchange: '',
    scheduledExchanges: {
      netTotal: 0,
      totalBooked: 0,
    },
    scheduledPayments: {
      netTotal: 0,
      totalBooked: 0,
    },
    currency: '',
    country: 'GB',
    status: AccountStatusType.active,
    customerId: '',
    organisationId: '',
    customerName: '',
    accountId: '',
    accountCreatedDate: '',
    accountIdentifier: [],
  };
}

export type MultiCurrencyAccountInfoType = RootAccountInfoType & {
  accountType: 'multi-currency';
  balance: {
    currency: string;
    amount: string;
  }[];
  availableBalance: {
    currency: string;
    amount: string;
  }[];
  indicativeBalance: {
    currency: string;
    amount: string;
  }[];
  // The exact balance held at the provider
  ledgerBalance: {
    currency: string;
    amount: string;
  }[];
  // The IF balance + the netchange in exchange that are pending ecluding debits if IF feature is turned on
  ledgerBalanceIncludingPendingExchange: {
    currency: string;
    amount: string;
  }[];
  currency: { currency: string }[];
};

export type SpendingBudget = {
  isActive: boolean;
  limit: number | null;
  spent: number | null;
  remaining: number | null;
  resetDay: number | null;
  periodStart: string | null;
  periodEnd: string | null;
};

export type CurrentAccountInfoType = RootAccountInfoType & {
  accountType: 'current';
  balance: string;
  availableBalance: string;
  /**
   * indicativeBalance - is the available balance plus the net amount of any pending exchanges.
   * So if the available balance was £200 and there were two exchange
   * orders one for sell £32 and another for buying £20 then the net result
   * here would be: `200.00 + (20.00 - 32.00) = £188.00`
   */
  indicativeBalance: string;
  // The exact balance held at the provider
  ledgerBalance: string;
  // The IF balance + the netchange in exchange that are pending ecluding debits if IF feature is turned on
  ledgerBalanceIncludingPendingExchange: string;
  currency: string;
  spendingBudget?: SpendingBudget;
};

export function isMultiCurrencyAccountInfoType(
  account: AccountInfoType | SavingsAccountInfoType,
): account is MultiCurrencyAccountInfoType {
  return account.accountType === 'multi-currency';
}

export type RootAccountInfoType = {
  accountType: 'current' | 'multi-currency' | 'savings';
  name: string;
  scheduledExchanges: {
    /**
     * `scheduledExchanges.netTotal` - Is the net cumulative total of all pending exchange orders
     * against that account or the sum of all credits and debits from pending exchange orders
     */
    netTotal: number;
    /**
     * `scheduledExchanges.totalBooked` - Is the net total all all pending exchange orders against
     * that account. i.e. how many change orders are currently pending
     */
    totalBooked: number;
  };
  scheduledPayments: {
    /**
     * `scheduledPayments.netTotal` - Is the net cumulative total of all pending transactions
     * against that account or the sum of all credits and debits from pending transactions
     */
    netTotal: number;
    /**
     * `scheduledPayments.totalBooked` - Is the net total all all pending transactions against
     * that account. i.e. how many change orders are currently pending
     */
    totalBooked: number;
  };
  alias: string;
  status: AccountStatusType;
  customerId: string;
  organisationId?: string;
  customerName: string;
  accountId: string;
  accountCreatedDate: string;
  accountIdentifier: AccountIdentifier[];
  country: string;
  parentAccountId?: string;
  ledgerNumber?: string;
  attributes?: { [key: string]: string };
  /** Provider-side accounts that receive incoming funds, one per currency (e.g. Airwallex Global Accounts). */
  receivingAccounts?: { providerAccountId: string; currency: string }[];
  /** Every currency's available balance converted into `currency` and summed; the app shows it as the account total. */
  totalAvailableBalance?: { currency: string; amount: string };
};

export type AccountInfoType = CurrentAccountInfoType | MultiCurrencyAccountInfoType;

export enum SavingsAccountTypeEnum {
  easyAccess = 'easy-access',
  fixedTerm = 'fixed-term',
}

export type SavingsAccountInfoType = {
  /** Literature for the exact product issue this account was opened on. */
  documents?: SavingsProductType['documents'];
  organisationId: string;
  clientName: string;
  accountId: string;
  accountType: 'easy-access' | 'fixed-term' | 'notice';
  productCode: string;
  productName: string;
  currency: string;
  country: string;
  accountName: string | null;
  accountIdentifier: AccountIdentifier[];
  startDate: string | null;
  endDate: string | null;
  interestRate: number;
  balance: string;
  availableBalance: string;
  status: AccountStatusType;
  interestAccrued: string | null;
  deposits: string | null;
  interestAtMaturity: string | null;
  balanceAtMaturity: string | null;
  annualEquivalantInterestRate: number | null;
  termMonths?: number;
  issueNumber?: number;
  isFundingEnabled?: boolean;
  isWithdrawalEnabled?: boolean;
  isClosureEnabled?: boolean;
  isRenewalEnabled?: boolean;
  maxDeposit?: string;
  minBalance?: string;
  /**
   * Last permitted funding date. A moving deadline on a fixed-term account: until the
   * first deposit it is the last day for that first deposit; once funded, the funding
   * window end.
   */
  lastFundingDate?: string;
  /**
   * Limited-access products only: how many withdrawals the pot's terms allow per
   * period, and (where the provider counts them) how many are left.
   *
   * Absence means the product is not limited-access, not that the allowance is
   * unknown. `limit` is the only field every provider can fill — thisbank derives
   * it from the product's `maxNrOfWithdrawal` and has no per-account count;
   * universal-sandbox publishes the live count as the account's `accessPolicy`.
   */
  withdrawalAllowance?: SavingsWithdrawalAllowanceType;
  metadata?: {
    [key: string]: unknown;
  };
};

export type SavingsWithdrawalAllowanceType = {
  limit: number;
  used?: number;
  remaining?: number;
  /** The provider's own period name, e.g. 'CALENDAR_YEAR'. */
  period?: string;
  resetsAt?: string;
};

export function isSavingsAccountInfoType(
  account: AccountInfoType | SavingsAccountInfoType,
): account is SavingsAccountInfoType {
  return (
    (account as SavingsAccountInfoType).accountType === 'easy-access' ||
    (account as SavingsAccountInfoType).accountType === 'fixed-term'
  );
}

export type NominatedAccountInfoDetailType = {
  accountId: string;
  accountType: 'nominated';
  currency: string;
  country: string;
  accountName: string | null;
  accountIdentifier: AccountIdentifier[];
  status: AccountStatusType;
  pispInstitutionId?: string;
};

export type MaturityDecisionRequest = {
  decision?: MaturityDecision;
  productCode?: string;
  advanced?: {
    strategy: 'REINVEST_FULL_BALANCE' | 'FULL_WITHDRAWAL' | 'INTEREST_ONLY_WITHDRAWAL' | 'CUSTOM';
    actions: Array<{
      type: 'REINVEST' | 'WITHDRAWAL';
      amount?: {
        amount: string;
        currency: string;
      };
      productCode?: string;
      productId?: string;
      productIssueId?: string;
    }>;
  };
};

export type SavingsAccountFilesType = {
  date?: string;
  file: string;
};

export type SavingsAccountFilesContentType = {
  mimeType: string;
  content: string;
};

export type FileDocumentResponse = {
  id: string;
  type: string;
  name: string;
  category?: string;
  entityId: string;
  fileId: string;
  createdAt: Date;
  updatedAt: Date;
  path?: string;
};

export type DownloadDocumentRequestType = {
  category: DocumentActionCategoryType;
  fileId: string;
};

interface DestinationIdentifier {
  id: string;
  country: string;
  currency: string;
  sortCode: string;
  accountNumber: string;
}
export interface ScheduledPaymentResponseType {
  id: string;
  data: {
    bid: string;
    name: string;
    amount: number;
    bankId: string;
    userId: string;
    currency: string;
    sortCode: string;
    accountType: string;
    accountNumber: string;
    sourceAccountId: string;
    paymentReference: string;
    transferReasonId: string;
    schedulePaymentDate: Date | string;
    transferReasonIdText: string;
    destinationIdentifier: DestinationIdentifier;
    paymentReasonCategory: string;
  };
}

export enum TransactionContextOptions {
  TRANSACTION = 'TRANSACTION',
  EXCHANGES = 'EXCHANGES',
  SAVINGS_TRANSACTION = 'SAVINGS_TRANSACTION',
  CARD_TRANSACTION = 'CARD_TRANSACTION',
  ALL_TRANSACTIONS = 'ALL_TRANSACTIONS',
  SCHEDULED_PAYMENTS = 'SCHEDULED_PAYMENTS',
}

export enum AccountContextOptions {
  NONE = 'NONE',
  CURRENT_ACCOUNT = 'CURRENT_ACCOUNT',
  SAVINGS_ACCOUNT = 'SAVINGS_ACCOUNT',
}

export class AccountTransactionRequest {
  public page = 1;
  public size = 5;
  public dateFrom?: string | null;
  public dateTo?: string | null;
  /** Order by transaction date; omitted = the backend's own default ('desc'). */
  public sort?: 'asc' | 'desc';
  public status?: TransactionStatus[];
  public type: TransactionType | CurrencyExchangeTransactionType = TransactionType.INCOMING;
  public transactionContext?: TransactionContextOptions;
  public accountContext?: AccountContextOptions;
  public currency?: string = '';
  public searchValue?: string = '';
}

export const TrxStatusOutgoingFilterOptions = [
  { name: 'Completed', value: TransactionStatus.COMPLETED, isActive: false },
  { name: 'Pending', value: TransactionStatus.PENDING, isActive: false },
  { name: 'Processing', value: TransactionStatus.PROCESSING, isActive: false },
  { name: 'Released', value: TransactionStatus.RELEASED, isActive: false },
  { name: 'Cancelled', value: TransactionStatus.CANCELLED, isActive: false },
  { name: 'Failed', value: TransactionStatus.FAILED, isActive: false },
];

export const TrxStatusIncomingFilterOptions = [
  { name: 'Completed', value: TransactionStatus.COMPLETED, isActive: false },
  { name: 'Pending', value: TransactionStatus.PENDING, isActive: false },
  { name: 'On-Hold', value: TransactionStatus.ON_HOLD, isActive: false },
  { name: 'Failed', value: TransactionStatus.FAILED, isActive: false },
];

export const TrxTypeFilterOptions = [
  { name: 'All', value: TransactionType.ALL, isActive: false },
  { name: 'Incoming', value: TransactionType.INCOMING, isActive: false },
  { name: 'Outgoing', value: TransactionType.OUTGOING, isActive: false },
];

export type SavingsProductType = {
  productName: string;
  accountType: 'easy-access' | 'fixed-term' | 'notice';
  productCode: string;
  interestRate: number;
  termMonths?: number | undefined;
  expiry?: string | undefined;
  aer: number;
  description?: string;
  productType?: string;
  minBalance?: string;
  maxDeposit?: string;
  providerExtraInfo?: {
    [key: string]: string | object | number | undefined | null | boolean;
  };
  documents?: {
    keyProductInformationUrl?: string;
    termsAndConditionsUrl?: string;
    interestRateTermsUrl?: string;
  };
  currency?: string;
};

export enum SavingsProductsCategory {
  RENEWAL = 'renewal',
  NEW_ACCOUNT = 'new-account',
}

export enum MaturityDecision {
  WITHDRAW = 'withdraw',
  RENEW = 'renew',
}
